import { DragEvent, FC, useState } from 'react';
import { CloseOutlined, HolderOutlined } from '@ant-design/icons';

import type { SelectedItemsPanelProps } from './customLens.types';

const SelectedItemsPanel: FC<SelectedItemsPanelProps> = ({
    title,
    emptyText,
    items,
    onReorder,
    onRemove,
    showRemoveButton = true,
    className = '',
    testIds,
}) => {
    const [draggingItem, setDraggingItem] = useState<string | null>(null);
    const [draggingOverItem, setDraggingOverItem] = useState<string | null>(null);

    const onDragStart = (event: DragEvent<HTMLLIElement>, item: string) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', item);
        setDraggingItem(item);
        setDraggingOverItem(item);
    };

    const onDragOver = (event: DragEvent<HTMLLIElement>, item: string) => {
        event.preventDefault();
        if (draggingOverItem !== item) setDraggingOverItem(item);
    };

    const onDrop = (event: DragEvent<HTMLLIElement>, targetItem: string) => {
        event.preventDefault();
        const draggedItem = event.dataTransfer.getData('text/plain') || draggingItem;
        if (!draggedItem || draggedItem === targetItem) {
            setDraggingItem(null);
            setDraggingOverItem(null);
            return;
        }
        const from = items.indexOf(draggedItem);
        const to = items.indexOf(targetItem);
        if (from === -1 || to === -1) {
            setDraggingItem(null);
            setDraggingOverItem(null);
            return;
        }
        const next = [...items];
        next.splice(from, 1);
        next.splice(to, 0, draggedItem);
        onReorder(next);
        setDraggingItem(null);
        setDraggingOverItem(null);
    };

    const onDragEnd = () => {
        setDraggingItem(null);
        setDraggingOverItem(null);
    };

    return (
        <div
            className={`flex min-h-0 flex-col overflow-hidden bg-backgroundGreyAlt p-4 ${className}`}
            data-testid={testIds?.panel}
        >
            <p className="mb-3 shrink-0 text-smallWord font-normal text-fontGreyAlt">{title}</p>
            {items.length === 0 ? (
                <p className="text-smallWord text-fontGreyAlt1">{emptyText}</p>
            ) : (
                <ul className="m-0 -mr-[10px] flex min-h-0 flex-1 list-none flex-col gap-2 overflow-y-auto p-0 pr-[10px]">
                    {items.map((name) => {
                        const isDropTarget = draggingOverItem === name && draggingItem !== name;
                        return (
                            <li
                                key={name}
                                draggable
                                data-testid={testIds?.item(name)}
                                onDragStart={(event) => onDragStart(event, name)}
                                onDragOver={(event) => onDragOver(event, name)}
                                onDrop={(event) => onDrop(event, name)}
                                onDragEnd={onDragEnd}
                                className={`mx-[12px] flex items-center gap-2 rounded-md border px-2 py-2 ${
                                    isDropTarget ? 'border-purpleFont' : 'border-[#ededee]'
                                } ${draggingItem === name ? 'opacity-70' : ''}`}
                            >
                                <HolderOutlined className="cursor-grab text-fontGreyAlt1 active:cursor-grabbing" />
                                <span className="flex-1 text-smallWord text-fontColor">{name}</span>
                                {showRemoveButton && onRemove ? (
                                    <button
                                        type="button"
                                        data-testid={testIds?.removeButton(name)}
                                        className="flex h-6 w-6 items-center justify-center rounded text-fontGreyAlt1 hover:bg-backgroundGreyAlt1 hover:text-fontColor"
                                        aria-label={`Remove ${name}`}
                                        onClick={() => onRemove(name)}
                                    >
                                        <CloseOutlined className="text-xs" />
                                    </button>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default SelectedItemsPanel;
